import { z } from 'zod';
import { bovedaSchema } from './boveda';
import { centavosPositivos, dia, motivo, textoRequerido, uuid } from './comunes';

const opcional = (max: number) => z.string().trim().max(max).nullable().optional();

export const proveedorSchema = z.object({
  nombre: textoRequerido(120),
  contacto: opcional(120),
  telefono: opcional(30),
  email: z.string().trim().email().max(200).nullable().optional().or(z.literal('')),
  rfc: opcional(20),
  direccion: opcional(300),
  notas: opcional(1000),
  activo: z.boolean().optional(),
});
export type ProveedorInput = z.infer<typeof proveedorSchema>;

export const margenSchema = z
  .object({ tipo: z.enum(['porcentaje', 'fijo']), valor: z.number().int().min(0) })
  .refine((m) => m.tipo !== 'porcentaje' || m.valor <= 100_000, { message: 'Porcentaje demasiado alto', path: ['valor'] });

export const crearProductoSchema = z.object({
  sku: z.string().trim().min(1).max(40).regex(/^[A-Za-z0-9._-]+$/, 'Usa letras, números, punto o guion'),
  nombre: textoRequerido(120),
  proveedorId: uuid.nullable().optional(),
  costo: centavosPositivos,
  margen: margenSchema,
  stockMinimo: z.number().int().min(0).default(0),
  vendible: z.boolean().default(true),
  comisionTipo: z.enum(['porcentaje', 'fijo']).default('porcentaje'),
  comisionValor: z.number().int().min(0).default(0),
  /** `null` = se compra por pieza; N = viene en paquetes de N piezas. */
  piezasPorPaquete: z.number().int().min(2).max(10_000).nullable().optional(),
});
export type CrearProductoInput = z.infer<typeof crearProductoSchema>;

export const actualizarProductoSchema = crearProductoSchema.omit({ costo: true, margen: true }).partial().extend({
  activo: z.boolean().optional(),
  margen: margenSchema.optional(),
  autorizacionToken: z.string().min(10).optional(),
  solicitarAlDueno: z.boolean().optional(),
});
export type ActualizarProductoInput = z.infer<typeof actualizarProductoSchema>;

export const costoSchema = z.object({ costo: centavosPositivos });

/** Cantidades y costos de una línea de compra: en piezas o en paquetes, nunca ambas. */
const camposLinea = {
  // En piezas (producto individual)…
  cantidad: z.number().int().min(1).max(100_000).optional(),
  costoUnitario: centavosPositivos.optional(),
  // …o en paquetes (producto en paquete): el total es paquetes × costoPaquete, exacto.
  paquetes: z.number().int().min(1).max(10_000).optional(),
  costoPaquete: centavosPositivos.optional(),
  aVenta: z.number().int().min(0),
  aUso: z.number().int().min(0),
};
type CamposLinea = { cantidad?: number; costoUnitario?: number; paquetes?: number; costoPaquete?: number; aVenta: number; aUso: number };
const piezasOPaquetes = (l: CamposLinea) => {
  const enPiezas = l.cantidad !== undefined && l.costoUnitario !== undefined;
  const enPaquetes = l.paquetes !== undefined && l.costoPaquete !== undefined;
  const algoDeLasDos = (l.cantidad ?? l.costoUnitario) !== undefined && (l.paquetes ?? l.costoPaquete) !== undefined;
  return (enPiezas || enPaquetes) && !algoDeLasDos;
};
const MSJ_PIEZAS_O_PAQUETES = { message: 'Indica piezas y costo por pieza, o paquetes y costo por paquete', path: ['cantidad'] };
const repartoCuadra = (l: CamposLinea) => l.cantidad === undefined || l.aVenta + l.aUso === l.cantidad;
const MSJ_REPARTO = { message: 'Lo asignado a venta y a uso debe sumar la cantidad comprada', path: ['aVenta'] };

export const compraSchema = z.object({
  /** De qué bóveda sale el pago de la compra. */
  boveda: bovedaSchema,
  proveedorId: uuid.nullable().optional(),
  fecha: dia.optional(),
  notas: z.string().trim().max(500).optional(),
  lineas: z
    .array(
      z
        .object({ productoId: uuid, ...camposLinea, margen: margenSchema.optional() })
        .refine(piezasOPaquetes, MSJ_PIEZAS_O_PAQUETES)
        .refine(repartoCuadra, MSJ_REPARTO),
    )
    .min(1),
  autorizacionToken: z.string().min(10).optional(),
});
export type CompraInput = z.infer<typeof compraSchema>;

/** Corrección de una compra: cada línea existente (por id) con sus cantidades y costos nuevos. */
export const correccionCompraSchema = z.object({
  lineas: z
    .array(z.object({ id: uuid, ...camposLinea }).refine(piezasOPaquetes, MSJ_PIEZAS_O_PAQUETES).refine(repartoCuadra, MSJ_REPARTO))
    .min(1),
});
export type CorreccionCompraInput = z.infer<typeof correccionCompraSchema>;

export const consumoSchema = z.object({ cantidad: z.number().int().min(1), motivo });
export const transferenciaSchema = z.object({ de: z.enum(['venta', 'uso']), a: z.enum(['venta', 'uso']), cantidad: z.number().int().min(1), motivo }).refine((t) => t.de !== t.a, {
  message: 'Elige bolsas distintas',
  path: ['a'],
});
export const ajusteSchema = z.object({ bolsa: z.enum(['venta', 'uso']), cantidad: z.number().int().refine((n) => n !== 0, 'No puede ser cero'), motivo });

export interface ProductoVista {
  id: string;
  sku: string;
  nombre: string;
  proveedor: { id: string; nombre: string } | null;
  costo: number;
  margenTipo: 'porcentaje' | 'fijo';
  margenValor: number;
  precioVenta: number;
  stockVenta: number;
  stockReservado: number;
  stockUso: number;
  stockMinimo: number;
  disponible: number;
  nivel: 'ok' | 'bajo' | 'agotado';
  vendible: boolean;
  activo: boolean;
  comisionTipo: 'porcentaje' | 'fijo';
  comisionValor: number;
  piezasPorPaquete: number | null;
}
